import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'antd';

import { capitalize } from '../../../helpers/common';
import { getFilterOptions, sortNumbers, sortStrings } from '../../../helpers/filter';
import { hasPermission } from '../../../helpers/user';

import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';

import { useModal } from '../../../hooks/useModal';

import { deleteGateway, fetchGateways } from '../../../redux/actions/gateway';
import { setSelectedFilters } from '../../../redux/reducers/filters';
import { setSortedData } from '../../../redux/reducers/gateway';

import AppModal from '../../../components/AppModal';
import AppTable from '../../../components/AppTable';
import { COLUMN_SIZE } from '../../../components/AppTable/constants';
import StatusIndicator from '../../../components/StatusIndicator';
import { errorToastHandler } from '../../../components/action_notifier';

import { SocketContext } from '../../../context/socket';

function GatewaysTable() {
  const { socket } = useContext(SocketContext);

  const dispatch = useDispatch();

  const { gateways, loading: gatewaysLoading } = useSelector(state => state.gateway);
  const { permissions } = useSelector(state => state.user);
  const { selectedFilters } = useSelector(state => state.filters);

  const [filterOptions, setFilterOptions] = useState({ connection: [] });
  const [filteredInfo, setFilteredInfo] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [state, setState] = useState({
    gatewaysQuery: '',
    ws: null
  });

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();

  useEffect(() => {
    getGateways();
  }, []);

  useEffect(() => {
    if (!gateways) return;

    setFilterOptions({ connection: getFilterOptions(gateways, 'status') });
  }, [gateways]);

  useEffect(() => {
    if (socket && !state.ws) {
      setState(prevState => ({ ...prevState, ws: socket }));

      socket.on('gatewayStatus', data => {
        setState(prevState => ({
          ...prevState,
          gateways: gateways?.map(item => {
            let status = item.status;

            if (data.ids.includes(item.id)) {
              status = data.status;
            }

            return { ...item, status };
          })
        }));
      });
    }

    return () => socket?.off('gatewayStatus');
  }, [socket]);

  const setFilters = (pagination, filters) => {
    setFilteredInfo(filters);
    dispatch(setSelectedFilters(filters));
  };

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilters(pagination, filters);
      dispatch(setSortedData(currentDataSource));
    },
    [setSortedData, dispatch]
  );

  const getGateways = async (query = '') => {
    try {
      await dispatch(fetchGateways({ query })).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  };

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const deleteSelectedGateways = async () => {
    try {
      await dispatch(deleteGateway({ ids: selectedIds })).unwrap();
      await getGateways();

      closeArchiveModal();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }
  };

  const setSelectedRecords = useCallback(selectedValues => {
    setSelectedIds(selectedValues);
  }, []);

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.GATEWAYS.DELETE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!selectedIds?.length}
            onClick={openArchiveModal}>
            Archive Gateway
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [selectedFilters, selectedIds]);

  const columns = [
    {
      title: 'Gateway ID',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      ellipsis: true,
      filteredValue: filteredInfo.identifier || null,
      fixed: 'left',
      searchable: true,
      sortDirections: ['ascend', 'descend'],
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier),
      render: (value, record) => <Link to={`gateway/${record.id}`}>{record.identifier}</Link>
    },
    {
      title: 'Gateway Name',
      dataIndex: 'name',
      configColumnKey: TABLE_CONFIG_COLUMNS.NAME,
      ellipsis: true,
      filteredValue: filteredInfo.name || null,
      searchable: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (a, b) => sortStrings(a.name, b.name),
      render: (value, record) => <Link to={`gateway/${record.id}`}>{value}</Link>
    },
    {
      title: 'Connection Status',
      dataIndex: 'status',
      configColumnKey: TABLE_CONFIG_COLUMNS.CONNECTION_STATUS,
      ellipsis: true,
      filteredValue: filteredInfo.status || null,
      filters: filterOptions.connection,
      sortDirections: ['ascend', 'descend'],
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => record?.status.startsWith(value),
      sorter: (a, b) => sortStrings(a?.status, b?.status),
      render: (value, record) => {
        const status = record?.status;
        const id = record?.id;

        return (
          record && (
            <div className="d-flex align-items-center">
              <StatusIndicator key={id} status={status} />
              {capitalize(status)}
            </div>
          )
        );
      }
    },
    {
      title: 'Connected Kraal Tags',
      dataIndex: 'connected_tags_count',
      configColumnKey: TABLE_CONFIG_COLUMNS.CONNECTED_KRAAL_TAGS,
      ellipsis: true,
      filteredValue: filteredInfo.connected_tags_count || null,
      searchable: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (a, b) => sortNumbers(a.connected_tags_count, b.connected_tags_count)
    }
  ];

  return (
    <>
      <AppTable
        headerClass="py-2"
        searchPlaceholder="Search Kraal Gateways"
        actions={actions}
        baseColumns={columns}
        dataSource={gateways}
        filterable={false}
        loading={gatewaysLoading}
        searchable={true}
        tableConfigKey={TABLE_CONFIG_KEYS.GATEWAYS}
        handleOnChange={handleTableChange}
        handleOnSelectRecords={setSelectedRecords}
        handleSearchChange={getGateways}
      />
      <AppModal
        isOpen={archiveModalVisible}
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive gateways"
        handleCancel={closeArchiveModal}
        handleConfirm={deleteSelectedGateways}>
        <div className="py-4">{messages.ARCHIVE_CONFIRMATION('gateways')}</div>
      </AppModal>
    </>
  );
}

export default memo(GatewaysTable);
